import { useState, useEffect } from 'react';
import { Bell, Check, Calendar, AlertTriangle, Info, RefreshCw, TrendingUp, FileText } from 'lucide-react';
import { clientApiService } from '../../services/clientApi';
import { Notification as ApiNotification } from '../../types/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useMyProjectUpdates } from '../../hooks/useClientApi';
import { useTexts } from '../../hooks/useTexts';

export default function NotificationsPage() {
  const { getText } = useTexts();
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('ALL');
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [activeTab, setActiveTab] = useState<'notifications' | 'updates'>('notifications');

  // Hook pour récupérer les actualités de projets
  const { data: projectUpdatesData, loading: projectUpdatesLoading } = useMyProjectUpdates();

  // Charger les notifications au montage du composant
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientApiService.getMyNotifications();
      setNotifications(response.results || []);
    } catch (err) {
      console.error('Erreur lors du chargement des notifications:', err);
      setError('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'PAIEMENT':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'ECHANCE':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'LIVRAISON':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PAIEMENT':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ECHANCE':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'LIVRAISON':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priorite: string) => {
    switch (priorite) {
      case 'URGENTE':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HAUTE':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'NORMALE':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const markAsRead = async (id: number) => {
    try {
      setMarkingAsRead(id.toString());
      await clientApiService.markNotificationAsRead(id);
      // Recharger les notifications pour avoir les données à jour
      await loadNotifications();
      // Notifier le Header de la mise à jour
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      console.error('Erreur lors du marquage de la notification:', err);
    } finally {
      setMarkingAsRead(null);
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAllAsRead(true);
      await clientApiService.markAllNotificationsAsRead();
      // Recharger les notifications pour avoir les données à jour
      await loadNotifications();
      // Notifier le Header de la mise à jour
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (err) {
      console.error('Erreur lors du marquage de toutes les notifications:', err);
    } finally {
      setMarkingAllAsRead(false);
    }
  };


  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'ALL') return true;
    if (filter === 'UNREAD') return !notif.lu;
    // if (filter === 'ACTION_REQUIRED') return notif.action_requise;
    if (filter === 'URGENTE') return notif.priorite === 'URGENTE';
    return notif.type_notification === filter;
  });

  const unreadCount = notifications.filter(n => !n.lu).length;
  // const actionRequiredCount = notifications.filter(n => n.action_requise).length;
  const urgentCount = notifications.filter(n => n.priorite === 'URGENTE').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600">Chargement des notifications...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage error={error || 'Une erreur est survenue'} />
          <div className="mt-4 text-center">
            <button
              onClick={loadNotifications}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2 inline" />
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('notifications.tab_notifications'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{getText('notifications.title')}</h1>
              <p className="text-gray-600 mt-2">
                {getText('notifications.description')}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {activeTab === 'notifications' && (
                <>
                  <button
                    onClick={loadNotifications}
                    className="flex items-center px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {getText('notifications.refresh')}
                  </button>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      disabled={markingAllAsRead}
                      className="flex items-center px-2 py-1 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                    >
                      {markingAllAsRead ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {getText('notifications.mark_all_read')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'notifications'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Bell className="w-4 h-4 mr-2" />
                {getText('notifications.tab_notifications')}
                {unreadCount > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('updates')}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'updates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                {getText('notifications.tab_updates')}
                {projectUpdatesData && projectUpdatesData.length > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    {projectUpdatesData.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'notifications' && (
          <>
            {/* Stats for Notifications */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Bell className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-gray-900">{notifications.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Bell className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Non lues</p>
                    <p className="text-2xl font-bold text-gray-900">{unreadCount}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Urgentes</p>
                    <p className="text-2xl font-bold text-gray-900">{urgentCount}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Lues</p>
                    <p className="text-2xl font-bold text-gray-900">{notifications.length - unreadCount}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters for Notifications */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'ALL', label: 'Toutes' },
                  { value: 'UNREAD', label: 'Non lues' },
                  { value: 'URGENTE', label: 'Urgentes' },
                  { value: 'PAIEMENT', label: 'Paiements' },
                  { value: 'ECHANCE', label: 'Échéances' },
                  { value: 'LIVRAISON', label: 'Livraisons' },
                  { value: 'GENERAL', label: 'Générales' }
                ].map((filterOption) => (
                  <button
                    key={filterOption.value}
                    onClick={() => setFilter(filterOption.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === filterOption.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notifications List */}
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 transition-all ${
                    !notification.lu ? 'border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notification.type_notification)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className={`text-lg font-semibold ${!notification.lu ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.titre}
                          </h3>
                          
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(notification.type_notification)}`}>
                            {notification.type_notification}
                          </span>
                          
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(notification.priorite)}`}>
                            {notification.priorite}
                          </span>
                          
                          {!notification.lu && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-3 leading-relaxed">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(notification.date_creation).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {!notification.lu && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                disabled={markingAsRead === notification.id.toString()}
                                className="flex items-center px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                              >
                                {markingAsRead === notification.id.toString() ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <Check className="w-4 h-4 mr-1" />
                                )}
                                Marquer comme lu
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State for Notifications */}
            {filteredNotifications.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Aucune notification
                </h3>
                <p className="text-gray-600 mb-6">
                  {filter === 'ALL' 
                    ? 'Vous n\'avez pas encore de notifications'
                    : 'Aucune notification ne correspond à ce filtre'
                  }
                </p>
                {filter !== 'ALL' && (
                  <button
                    onClick={() => setFilter('ALL')}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Voir toutes les notifications
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'updates' && (
          <>
            {/* Stats for Project Updates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Total actualités</p>
                    <p className="text-2xl font-bold text-gray-900">{projectUpdatesData ? projectUpdatesData.length : 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Avancements</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {projectUpdatesData ? projectUpdatesData.filter(u => u.type_mise_a_jour === 'AVANCEMENT').length : 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Info className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Livraisons</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {projectUpdatesData ? projectUpdatesData.filter(u => u.type_mise_a_jour === 'LIVRAISON').length : 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Updates List */}
            <div className="space-y-4">
              {projectUpdatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                  <span className="ml-3 text-gray-600">Chargement des actualités...</span>
                </div>
              ) : projectUpdatesData && projectUpdatesData.length > 0 ? (
                projectUpdatesData.map((update) => (
                  <div key={update.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-xl font-semibold text-gray-900">
                            {update.titre}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            update.type_mise_a_jour === 'AVANCEMENT' ? 'bg-green-100 text-green-800' :
                            update.type_mise_a_jour === 'LIVRAISON' ? 'bg-blue-100 text-blue-800' :
                            update.type_mise_a_jour === 'RETARD' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {update.type_mise_a_jour}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            update.priorite === 'HAUTE' ? 'bg-orange-100 text-orange-800' :
                            update.priorite === 'NORMALE' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {update.priorite}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {update.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(update.date_publication).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                          {update.pourcentage_avancement && (
                            <div className="flex items-center text-sm">
                              <span className="text-gray-600 mr-2">Avancement:</span>
                              <span className="font-medium text-blue-600">
                                {update.pourcentage_avancement}%
                              </span>
                            </div>
                          )}
                        </div>
                        {update.pourcentage_avancement && (
                          <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                              <span>Progression du projet</span>
                              <span>{update.pourcentage_avancement}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${update.pourcentage_avancement}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Aucune actualité récente
                  </h3>
                  <p className="text-gray-600">
                    Les mises à jour de vos projets apparaîtront ici
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}